import React, { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ActionButton } from '@adobe/react-spectrum';
import { MomentData, LinkedMoment } from '../types';
import {
  getCellValue,
  formatPhaseDateShort,
  getPhaseAbbreviation
} from './utils';
import { RiskLevelReadOnly } from './RiskLevelReadOnly';
import { TooltipCell } from './TooltipCell';
import { CommonTooltip } from './CommonTooltip';
import { isEolMoment } from 'utils/constants';
import { CardTitleDisplay } from 'app/components/TimelineCard/CardTitleDisplay';
import { formatEolPhaseDisplayTitle } from 'utils/eolPhaseTitlePrefix';
import ChevronIcon from 'app/assets/svgIcons/ChevronIcon';
import ItemCount from './ItemCount';
import styles from './MetaRow.module.scss';
import { buildPathWithPreservedViewState } from 'utils/sharedViewMode';

interface MetaRowProps {
  moment: MomentData | LinkedMoment;
  isLinked?: boolean;
  columnsConfig: any[];
  hoveredRow: string | null;
  expandedRows: Set<string>;
  rowHoveredClass: string;
  onRowHover: (issueId: string) => void;
  onRowLeave: () => void;
  onToggleRow: (issueId: string) => void;
}

const MetaRow: React.FC<MetaRowProps> = memo(
  ({
    moment,
    isLinked = false,
    columnsConfig,
    hoveredRow,
    expandedRows,
    rowHoveredClass,
    onRowHover,
    onRowLeave,
    onToggleRow
  }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const hasLinkedMoments =
      'linkedMoments' in moment &&
      moment.linkedMoments &&
      moment.linkedMoments.length > 0;
    const isExpanded = expandedRows.has(moment.issueId);
    const isHovered = hoveredRow === moment.issueId;

    // Compute phase abbreviation once for the entire row
    const phaseName = (moment as MomentData | LinkedMoment).phaseName;
    const phaseAbbr = getPhaseAbbreviation(phaseName);
    const momentType = moment.metaDetails?.momentType;

    const formatNameCell = (rawName: string) => {
      if (isEolMoment(momentType)) {
        return (
          <CardTitleDisplay
            projectType={momentType}
            releasePhase={phaseName}
            title={rawName}
            displayTitle={rawName}
            className={styles.nameText}
          />
        );
      }
      return rawName;
    };

    const formatNameTooltip = (rawName: string) =>
      isEolMoment(momentType)
        ? formatEolPhaseDisplayTitle(phaseName, rawName)
        : rawName;

    const handleMomentClick = () => {
      navigate(
        buildPathWithPreservedViewState(
          `/milestones/moments/${encodeURIComponent(moment.issueId)}`,
          location.search
        )
      );
    };

    return (
      <tr
        className={`${isHovered ? rowHoveredClass : ''} ${
          isLinked ? 'linked-row' : ''
        }`}
        onMouseEnter={() => onRowHover(moment.issueId)}
        onMouseLeave={onRowLeave}
        data-testid={`milestone-grid-row-${moment.issueId}`}
      >
        {columnsConfig.map((column, index) => {
          const cellStyle = column.width ? { width: column.width } : undefined;

          // Risk level column with colored indicator
          if (column.key === 'riskLevel') {
            // Show empty cell for linked rows (risk level will be shown in name column)
            if (isLinked) {
              return (
                <td
                  key={column.key}
                  data-column-key={column.key}
                  style={cellStyle}
                  data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
                ></td>
              );
            }
            const riskLevel = getCellValue(moment, column.field);
            return (
              <td
                key={column.key}
                data-column-key={column.key}
                style={cellStyle}
                data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
              >
                <RiskLevelReadOnly
                  riskLevel={riskLevel}
                  testId={`milestone-grid-risk-${moment.issueId}`}
                />
              </td>
            );
          }

          // Name column with expand/collapse button (only for main rows, not linked)
          if (index === 1 && !isLinked) {
            const cellValue = getCellValue(moment, column.field);
            const linkedMomentsCount =
              'linkedMoments' in moment && moment.linkedMoments
                ? moment.linkedMoments.length
                : 0;
            return (
              <td
                key={column.key}
                data-column-key={column.key}
                style={cellStyle}
                data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
              >
                <div className={styles.nameContainer}>
                  {hasLinkedMoments && (
                    <button
                      onClick={() => onToggleRow(moment.issueId)}
                      className={styles.expandButton}
                      data-testid={`milestone-grid-expand-${moment.issueId}`}
                    >
                      <ChevronIcon isExpanded={isExpanded} />
                    </button>
                  )}
                  <TooltipCell
                    value={formatNameCell(cellValue)}
                    tooltipContent={formatNameTooltip(cellValue)}
                    className={styles.nameText}
                    onClick={handleMomentClick}
                    testId={`milestone-grid-name-${moment.issueId}`}
                  />
                  {phaseAbbr && (
                    <CommonTooltip tooltipContent={phaseName}>
                      <ActionButton
                        isQuiet
                        UNSAFE_className={styles.phaseAbbr}
                        data-testid={`milestone-grid-phase-${moment.issueId}`}
                      >
                        {phaseAbbr}
                      </ActionButton>
                    </CommonTooltip>
                  )}
                  {linkedMomentsCount > 0 && (
                    <ItemCount
                      count={linkedMomentsCount}
                      testId={`milestone-grid-count-${moment.issueId}`}
                    />
                  )}
                </div>
              </td>
            );
          }

          // Name column for linked rows (combine with risk level)
          if (index === 1 && isLinked) {
            const cellValue = getCellValue(moment, column.field);
            const riskLevel = getCellValue(moment, 'metaDetails.riskLevel');
            return (
              <td
                key={column.key}
                data-column-key={column.key}
                style={cellStyle}
                data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
              >
                <div className={styles.linkedNameContainer}>
                  <RiskLevelReadOnly
                    riskLevel={riskLevel}
                    noMargin={true}
                    testId={`milestone-grid-risk-linked-${moment.issueId}`}
                  />
                  <TooltipCell
                    value={formatNameCell(cellValue)}
                    tooltipContent={formatNameTooltip(cellValue)}
                    className={styles.nameText}
                    onClick={handleMomentClick}
                    testId={`milestone-grid-name-linked-${moment.issueId}`}
                  />
                  {phaseAbbr && (
                    <CommonTooltip tooltipContent={phaseName}>
                      <ActionButton
                        isQuiet
                        UNSAFE_className={styles.phaseAbbr}
                        data-testid={`milestone-grid-phase-linked-${moment.issueId}`}
                      >
                        {phaseAbbr}
                      </ActionButton>
                    </CommonTooltip>
                  )}
                </div>
              </td>
            );
          }

          // Date column with formatted display (no tooltip)
          if (column.key === 'date') {
            const dateValue = getCellValue(moment, column.field);
            const formattedDate = dateValue
              ? formatPhaseDateShort(dateValue)
              : '';
            return (
              <td
                key={column.key}
                data-column-key={column.key}
                style={cellStyle}
                data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
              >
                {formattedDate}
              </td>
            );
          }

          // Tier column (no tooltip)
          if (column.key === 'tier') {
            const cellValue = getCellValue(moment, column.field);
            return (
              <td
                key={column.key}
                data-column-key={column.key}
                style={cellStyle}
                data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
              >
                {cellValue}
              </td>
            );
          }

          // Regular columns (with tooltip)
          const cellValue = getCellValue(moment, column.field);
          return (
            <td
              key={column.key}
              data-column-key={column.key}
              style={cellStyle}
              data-testid={`milestone-grid-cell-${moment.issueId}-${column.key}`}
            >
              <TooltipCell
                value={cellValue}
                testId={`milestone-grid-cell-value-${moment.issueId}-${column.key}`}
              />
            </td>
          );
        })}
      </tr>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for better performance
    const isExpanded = prevProps.expandedRows.has(prevProps.moment.issueId);
    const wasExpanded = nextProps.expandedRows.has(nextProps.moment.issueId);

    return (
      prevProps.moment.issueId === nextProps.moment.issueId &&
      prevProps.moment.milestoneId === nextProps.moment.milestoneId &&
      prevProps.hoveredRow === nextProps.hoveredRow &&
      prevProps.isLinked === nextProps.isLinked &&
      isExpanded === wasExpanded
    );
  }
);

MetaRow.displayName = 'MetaRow';

export default MetaRow;
